import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import bookingActions from '../actions';
import jobActions from '../../../actions';
import { sendRequest } from '../../../../../../helpers/saga';
import { logger } from '../../../../../../helpers/logger';
import notifications from '../../../../../../redux/notifications/actions';
import Api from '../../../../../../helpers/api';
import { keyByProperty } from '../../../../../../helpers/core';
import { selectIsPriorityPricingEnabled } from '../../../../../../helpers/priorityPricing';
import { t } from '../../../../../../i18n';

const updateApi = new Api('jobs/:id/bookings/:id/');
const updatePastJobApi = new Api('jobs/:id/bookings/:id/update_workers_required');
const overtimeWarningsApi = new Api('jobs/:id/bookings/:id/overtime_warning');
const createApi = new Api('jobs/:id/bookings');
const createFormDataApi = new Api('jobs/:id/bookings/create_form_data');
const updateFormDataApi = new Api('jobs/:id/bookings/:id/update_form_data');
const updateJobGroupsVmsIdApi = new Api('job_groups/:id/skills/:id/update_vendor_management_system_id');
const updateJobVmsIdApi = new Api('jobs/:id/bookings/:id/update_vendor_management_system_id');
const checkerApi = new Api('jobs/:id/cancellation_policies/checker');

function* updatePastJob({ payload }) {
  const { jobId, bookingId, workers_required } = payload;

  try {
    const result = yield call(
      sendRequest,
      updatePastJobApi.updateItem,
      { data: { booking: { workers_required } }, id: [jobId, bookingId] },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(actions.updatePastJobSuccess(result.data));
      yield put(bookingActions.disableBookingActions(false));
      yield put(jobActions.loadData({ id: jobId }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.jobs.bookings.booking_label'),
          }),
        }),
      );
      yield put(actions.clearStore());
    } else {
      yield put(actions.updatePastJobError());
      yield put(bookingActions.disableBookingActions(false));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* checkIfCancellationPolicyMayApplyRequest({ payload }) {
  const { jobId, bookingId, position_id, level, workers_required } = payload;

  try {
    const result = yield call(
      sendRequest,
      checkerApi.updateItem,
      { data: { bookings: [{ id: bookingId, position_id, level, workers_required }] }, id: jobId },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(
        actions.checkCancellationPoliciesSuccess({
          cancellation_policy_may_apply: result.data.cancellation_policy_may_apply,
        }),
      );
      yield put(actions.next());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateJobVmsId({ payload }) {
  const { jobId, bookingId, vmsInputValue } = payload;

  try {
    const result = yield call(
      sendRequest,
      updateJobVmsIdApi.updateItem,
      { data: { vendor_management_system_id: vmsInputValue }, id: [jobId, bookingId] },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(actions.updateJobVmsIdSuccess());
      yield put(jobActions.loadData({ id: jobId }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.jobs.bookings.vms_id_label'),
          }),
        }),
      );
    } else {
      yield put(actions.updateJobVmsIdError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateJobGroupsVmsId({ payload }) {
  const { skillId, vmsInputValue, jobGroupId, jobId } = payload;

  try {
    const result = yield call(
      sendRequest,
      updateJobGroupsVmsIdApi.updateItem,
      { data: { vendor_management_system_id: vmsInputValue }, id: [jobGroupId, skillId] },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(actions.updateJobGroupsVmsIdSuccess());
      yield put(jobActions.loadData({ id: jobId }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.jobs.bookings.vms_id_label'),
          }),
        }),
      );
    } else {
      yield put(actions.updateJobGroupsVmsIdError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateBooking({ payload }) {
  try {
    const { editForm, id, jobFulfilled, cancellation_policy_may_apply, cancellation } = payload;

    const propsArray = Object.values(editForm);
    const booking = keyByProperty(propsArray, 'name', prop => prop.value);

    const { default_shift_payouts, pantsAttireItems, shirtsAttireItems, shoesAttireItems, ...otherProps } = booking;

    const defaultShiftPayouts = default_shift_payouts.map(item => {
      const defaultShiftPayoutsPropsArray = Object.values(item);

      return {
        // eslint-disable-next-line no-underscore-dangle
        ...(item._destroy && { _destroy: item._destroy }),
        ...keyByProperty(defaultShiftPayoutsPropsArray, 'name', prop => prop.value),
        organization_id: item?.organization?.id?.value,
      };
    });

    const isPriorityPricingEnabled = yield select(selectIsPriorityPricingEnabled);

    const propsToSend = {
      ...otherProps,
      attire_items: [pantsAttireItems, shirtsAttireItems, shoesAttireItems].filter(item => item),
      default_shift_payouts_attributes: defaultShiftPayouts,
      recalculate_priority_multiplier: isPriorityPricingEnabled && Boolean(otherProps.recalculate_priority_multiplier),
      reset_priority_multiplier:
        isPriorityPricingEnabled && !otherProps.recalculate_priority_multiplier && otherProps.priority_multiplier > 1,
    };

    if (jobFulfilled) {
      propsToSend.notify_invitees = false;
    }

    if (!propsToSend.workers_required) {
      propsToSend.workers_required = 0;
    }

    if (cancellation_policy_may_apply) {
      propsToSend.cancellation = { fee_accepted: cancellation.fee_accepted };
    }

    const result = yield call(
      sendRequest,
      updateApi.updateItem,
      { data: { booking: propsToSend }, id },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        yield put(actions.saveSuccess({ data: booking }));
        yield put(bookingActions.disableBookingActions(false));
        yield put(jobActions.loadData({ id: id[0] }));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t('dash.jobs.bookings.booking_label'),
            }),
          }),
        );
        yield put(actions.clearStore());
      } else {
        yield put(actions.saveError({ data: result }));
        yield put(bookingActions.disableBookingActions(false));
      }
    } else {
      yield put(bookingActions.disableBookingActions(false));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* createBooking({ payload }) {
  try {
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const booking = keyByProperty(propsArray, 'name', prop => prop.value);
    const { default_shift_payouts, ...otherProps } = booking;

    const defaultShiftPayouts = default_shift_payouts.map(item => {
      const defaultShiftPayoutsPropsArray = Object.values(item);

      return {
        ...keyByProperty(defaultShiftPayoutsPropsArray, 'name', prop => prop.value),
        organization_id: item?.organization?.id?.value,
      };
    });

    // remove notifications in case of adding a gig
    delete otherProps.notify_client;
    delete otherProps.notify_workers;
    delete otherProps.notify_invitees;

    const isPriorityPricingEnabled = yield select(selectIsPriorityPricingEnabled);

    const propsToSend = {
      ...otherProps,
      default_shift_payouts_attributes: defaultShiftPayouts,
      recalculate_priority_multiplier: isPriorityPricingEnabled && Boolean(otherProps.recalculate_priority_multiplier),
      reset_priority_multiplier:
        isPriorityPricingEnabled && !otherProps.recalculate_priority_multiplier && otherProps.priority_multiplier > 1,
    };

    const result = yield call(
      sendRequest,
      createApi.createItem,
      { data: { booking: propsToSend }, id },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(jobActions.toggleAddGigModal());

      if (result.data) {
        yield put(actions.saveSuccess({ data: booking }));
        yield put(jobActions.loadData({ id }));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.added_successfully', {
              item: t('dash.jobs.bookings.new_booking_group_label'),
            }),
          }),
        );
        yield put(actions.clearStore());
      } else {
        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const { job_id, data, booking_id, step } = payload;

    const result = data
      ? yield call(
          sendRequest,
          booking_id ? updateFormDataApi.load : createFormDataApi.load,
          { id: booking_id ? [job_id, booking_id] : job_id },
          notifications.REQUEST_ERROR,
        )
      : yield call(sendRequest, createFormDataApi.load, { id: job_id }, notifications.REQUEST_ERROR);

    if (result) {
      yield put(actions.loadFormData({ data: result.data, step }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* sendOvertimeWarning({ payload }) {
  try {
    const { id, starts_at, finishes_at, workers_required } = payload;
    const booking = { starts_at, finishes_at, workers_required };
    const result = yield call(
      sendRequest,
      overtimeWarningsApi.createItem,
      { data: { booking }, id },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      const { data } = result;
      yield put(actions.overtimeWarningSuccess(data));
    }
  } catch (error) {
    logger.error(error);
  }
}

export {
  updateBooking,
  createBooking,
  loadFormData,
  sendOvertimeWarning,
  updateJobVmsId,
  updateJobGroupsVmsId,
  checkIfCancellationPolicyMayApplyRequest,
};
export default function* rootSaga() {
  yield all([
    takeEvery(actions.SAVE, updateBooking),
    takeEvery(actions.ADD, createBooking),
    takeEvery(actions.LOAD, loadFormData),
    takeEvery(actions.OVERTIME_WARNING, sendOvertimeWarning),
    takeEvery(actions.UPDATE_JOB_GROUPS_VMS_ID_REQUEST, updateJobGroupsVmsId),
    takeEvery(actions.UPDATE_JOB_VMS_ID_REQUEST, updateJobVmsId),
    takeEvery(actions.CHECK_CANCELLATION_POLICIES_REQUEST, checkIfCancellationPolicyMayApplyRequest),
    takeEvery(actions.UPDATE_PAST_JOB_REQUEST, updatePastJob),
  ]);
}
